import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, PageHero, Eyebrow } from "@/components/Motion";
import { IMAGES } from "@/data/content";
import { useLanguage } from "@/i18n";

export default function ChiSiamo() {
  const { t, content } = useLanguage();

  return (
    <main data-testid="chi-siamo-page">
      <PageHero
        eyebrow={t("aboutHeroEyebrow")}
        lines={[t("aboutL1"), t("aboutL2")]}
        sub={t("aboutSub")}
        img={IMAGES.cantiere}
      />

      <section className="mx-auto max-w-7xl px-6 py-24 md:py-32">
        <Reveal>
          <Eyebrow>{t("manifesto")}</Eyebrow>
        </Reveal>
        <div className="mt-14 space-y-24 md:space-y-36">
          {content.chapters.map((chapter, i) => (
            <motion.div
              key={chapter.num}
              data-testid={`chapter-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className={`grid items-center gap-10 md:grid-cols-12 md:gap-14`}
            >
              <div className={`md:col-span-5 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                <div className="group relative overflow-hidden">
                  <img
                    src={chapter.img}
                    alt={chapter.title}
                    className="img-duotone aspect-[6/5] w-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                <div className="flex items-baseline gap-5">
                  <span className="font-serif text-6xl italic leading-none text-[#8F6B3C]/50 md:text-8xl">
                    {chapter.num}
                  </span>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone-500">
                      {t("chapterLabel")} {chapter.num}
                    </p>
                    <h2 className="mt-2 font-serif text-3xl uppercase leading-tight tracking-tight text-stone-900 md:text-4xl">
                      {chapter.title}
                    </h2>
                  </div>
                </div>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-stone-500 md:text-lg">
                  {chapter.text}
                </p>
                <span className="mt-8 block h-px w-24 bg-[#C89D66]/50" />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section data-testid="values-section" className="border-t border-stone-300/70 bg-[#F0ECE4]">
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
          <Reveal>
            <Eyebrow>{t("valuesEyebrow")}</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl uppercase leading-tight tracking-tight text-stone-900 md:text-5xl">
              {t("valuesTitleA")} <span className="italic text-[#8F6B3C]">{t("valuesTitleB")}</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px bg-stone-300/60 sm:grid-cols-2 lg:grid-cols-4">
            {content.pillars.map((pillar, i) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="group bg-[#F0ECE4] p-8 transition-colors duration-500 hover:bg-[#FFFFFF]"
              >
                <span className="font-mono text-xs tracking-[0.25em] text-[#8F6B3C]/70">
                  0{i + 1}
                </span>
                <h3 className="mt-5 font-serif text-xl uppercase tracking-wide text-stone-900">
                  {pillar.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-stone-500">{pillar.desc}</p>
              </motion.div>
            ))}
          </div>
          <Reveal className="mt-16 text-center">
            <Link
              data-testid="about-cta-projects"
              to="/progetti"
              className="group inline-flex items-center gap-3 border border-stone-300 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-900 transition-colors duration-300 hover:border-[#C89D66] hover:text-[#8F6B3C]"
            >
              {t("aboutCta")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
